import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { object, string } from "yup";

import Button from "components/Button";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import { routes } from "constants/routes";
import { VEHICLE_NUMBER_TYPES } from "constants/signup";
import CarrierSelector from "features/main/HaulPay/Shared/CarrierSelector";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { IPaymentProfile } from "models/HaulPay";
import { AddressInfo } from "models/User";
import { createPaymentProfileAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

interface IConnectCarrierForm {
  identifyNumber: string;
  owner_email: string;
  owner_name_first: string;
  owner_name_last: string;
  owner_phone_number_cell: string;
  company_name: string;
  businessAddress: AddressInfo;
}

type CreateProfileTypes = {
  profile: { email: string; phone_number: string };
  handleNext: VoidFunction;
  setSelectedPaymentProfile: React.Dispatch<React.SetStateAction<IPaymentProfile | undefined>>;
};

export default function CreateProfile({
  profile,
  handleNext,
  setSelectedPaymentProfile
}: Readonly<CreateProfileTypes>) {
  const {
    handleSubmit,
    setValue,
    register,
    control,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm<IConnectCarrierForm>({
    resolver: yupResolver(
      object({
        identifyNumber: string().required("This field is required"),
        owner_email: string().required("This field is required"),
        owner_name_first: string().required("This field is required"),
        owner_name_last: string().required("This field is required"),
        owner_phone_number_cell: string().required("This field is required"),
        company_name: string().required("This field is required"),
        businessAddress: object().required("This field is required")
      })
    ),
    defaultValues: {
      owner_email: profile.email,
      owner_phone_number_cell: profile.phone_number
    }
  });

  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [selectedCarrierType, setSelectedCarrierType] = useState(VEHICLE_NUMBER_TYPES[0]);

  const setPaymentProfile = (item: IPaymentProfile) => {
    setSelectedPaymentProfile(item);
    handleNext();
  };

  function goBack() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const updateFMCSAInfo = async (data: any) => {
    if (data) {
      const { address, city, country, state, zip, dba, phone } = data;
      const businessAddress: AddressInfo = {
        address,
        city,
        country,
        state,
        zip
      };
      setValue("company_name", dba);
      setValue("businessAddress", businessAddress);
      setValue("owner_phone_number_cell", phone);
    }
  };

  const submitProfile = async (data: IConnectCarrierForm) => {
    setIsCreatingProfile(true);
    try {
      const { data: profile } = await createPaymentProfileAPI({
        [`company_${selectedCarrierType.value}`]: data.identifyNumber,
        owner_address_city: data.businessAddress.city,
        owner_address_country: data.businessAddress.country,
        owner_address_state: data.businessAddress.state,
        owner_address_street_one: data.businessAddress.address,
        owner_address_zip: data.businessAddress.zip,
        company_address_street_one: data.businessAddress.address,
        company_address_city: data.businessAddress.city,
        company_address_state: data.businessAddress.state,
        company_address_zip: data.businessAddress.zip,
        owner_email: data.owner_email,
        owner_phone_number_cell: data.owner_phone_number_cell,
        [selectedCarrierType.value]: data.identifyNumber,
        company_name: data.company_name,
        address: {
          street_one: data.businessAddress.address,
          city: data.businessAddress.city,
          state: data.businessAddress.state,
          zip: data.businessAddress.zip,
          country: data.businessAddress.country
        },
        owner_name_first: data.owner_name_first,
        owner_name_last: data.owner_name_last
      });
      setSelectedPaymentProfile(profile);
      handleNext();
    } catch (err: any) {
      showErrorMessage(err.message);
    } finally {
      setIsCreatingProfile(false);
    }
  };

  return (
    <form className={styles.createProfileContainer} onSubmit={handleSubmit(submitProfile)}>
      <div className={styles.body}>
        <div className={styles.header}>
          <h3 className={styles.title}>Create Carrier Payment Profile</h3>
          <span className="text-tertiary">
            If the carrier has not been able to complete the onboarding themselves this enables you
            to create their payment profile for them to be reviewed.
          </span>
        </div>

        <div className={styles.inputList}>
          <CarrierSelector
            register={register}
            errors={errors}
            getValues={getValues}
            setPaymentProfile={setPaymentProfile}
            selectedCarrierType={selectedCarrierType}
            setSelectedCarrierType={setSelectedCarrierType}
            fmcsaEnabled
            updateFMCSAInfo={updateFMCSAInfo}
          />

          <Input
            type="email"
            name="owner_email"
            register={register}
            disabled={!!profile.email}
            label="&nbsp;&nbsp;Carrier Email"
          />
          <Input
            name="owner_name_first"
            register={register}
            error={errors.owner_name_first}
            label="&nbsp;&nbsp;First Name"
          />
          <Input
            name="owner_name_last"
            register={register}
            error={errors.owner_name_last}
            label="&nbsp;&nbsp;Last Name"
          />
          <Controller
            name="owner_phone_number_cell"
            control={control}
            render={({ field: { value = "", onChange } }) => (
              <PhoneNumber
                value={value}
                onChange={onChange}
                placeholder="Phone Number"
                disableCountryCode
                disabled={!!profile.phone_number}
                error={errors.owner_phone_number_cell}
                className="flex-1"
                wrapperClass="flex-1"
                data-cy="phonenumber-input"
              />
            )}
          />
          <Input name="company_name" register={register} label="&nbsp;&nbsp;Company Name" />
          <Controller
            name="businessAddress"
            control={control}
            render={({ field: { value = null, onChange }, fieldState: { error } }) => (
              <GoogleAutoComplete
                label="Business Address"
                placeholder="Your Address"
                error={error}
                value={value}
                onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
                data-cy="signup-address-input"
              />
            )}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          variant="outline"
          rounded
          size="lg"
          onClick={goBack}
          className="desktop:!rounded-base desktop:!w-48">
          {isDesktop ? "Cancel" : "CANCEL"}
        </Button>
        <Button
          rounded
          size="lg"
          type="submit"
          disabled={isSubmitting || isCreatingProfile}
          className="desktop:!rounded-base desktop:!w-48">
          {isSubmitting || isCreatingProfile ? (
            <Icon path={mdiLoading} size={1.5} spin={1} />
          ) : isDesktop ? (
            "Next"
          ) : (
            "NEXT"
          )}
        </Button>
      </div>
    </form>
  );
}
